import { ChangeDetectionStrategy, OnInit, OnDestroy, Component, EventEmitter, Output, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { FilterTypes } from "src/app/modules/shared/enums/filterTypes";
import { OperationTypes } from "src/app/modules/shared/enums/operationTypes";
import { WtValidators } from "src/app/modules/shared/reactiveValidators/wtValidators";
import { AlertService } from "src/app/modules/shared/services/alert.service";
import { AllowedFiltersService } from "src/app/modules/shared/services/allowed-filters.service";
import { AuthenticationService } from "src/app/modules/shared/services/authentication.service";
import { ConfirmationService } from "src/app/modules/shared/services/confirmation.service";
import { EmployeeSettingsService } from "src/app/modules/shared/services/employee-settings.service";
import { EmployeeDetailsViewModel } from "src/app/modules/shared/viewModels/employeeDetailsViewModel";
import { T } from "src/assets/i18n/translation-keys";
import { ManageEmployeeComponent } from "../manage-employee/manage-employee.component";
import { FilterViewModel } from "src/app/modules/shared/models/filter/filterViewModel";
import { TranslateService } from "@ngx-translate/core";

enum Options {
  existingUser = 'existingUser',
  newUser = 'newUser',
}

const FORM_FIELDS = ['firstName', 'surname', 'username'] as const;
type ExternalUserFormModel = Pick<EmployeeDetailsViewModel, typeof FORM_FIELDS[number]>;
type ExternalUserFormControls = {
  [K in keyof ExternalUserFormModel]: FormControl<ExternalUserFormModel[K]>;
};

@Component({
    selector: 'app-manage-external-user',
    templateUrl: './manage-external-user.component.html',
    styleUrls: ['./manage-external-user.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ManageExternalUserComponent implements OnInit, OnDestroy {
  public employeeDetailsViewModel: EmployeeDetailsViewModel;
  public editMode: boolean;
  public headerText: string;
  public saveButtonText: string;
  public form: FormGroup<ExternalUserFormControls>;
  public saveButtonLoader: boolean;
  public selectedOption: Options = Options.newUser;
  public useOptions: boolean;
  public filterTypes = FilterTypes;
  public filters: FilterViewModel[] = [];
  public currentlySelectedExternalUserId: string | number;
  public readonly Options = Options;
  public readonly T = T;
  @Output() onDelete = new EventEmitter<{ employee: EmployeeDetailsViewModel, promoteToEmployee: boolean }>();
  @Output() onAdd = new EventEmitter<EmployeeDetailsViewModel>();
  @Output() onEdit = new EventEmitter<EmployeeDetailsViewModel>();
  @Output() onAddExistingUser = new EventEmitter<FilterViewModel[]>();
  @Output() onCreateAndAddExternalUser = new EventEmitter<EmployeeDetailsViewModel>();

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly fb: FormBuilder,
    private readonly wtValidators: WtValidators,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly confirmationService: ConfirmationService,
    private readonly alertService: AlertService,
    private readonly modalService: BsModalService,
    private readonly authenticationService: AuthenticationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translationService: TranslateService,
  ) { }

  ngOnInit(): void {
    this.employeeDetailsViewModel = this.employeeDetailsViewModel || new EmployeeDetailsViewModel();
    this.headerText = this.editMode ? this.translationService.instant(T.settings.employees_departments_org.edit_external_user) : this.translationService.instant(T.settings.employees_departments_org.add_external_user);
    this.setSaveButtonText();
    this.initForm();
    this.initializeFilters();
  }

  ngOnDestroy(): void { }

  public onClose(): void {
    this.bsModalRef.hide();
  }

  public deleteClicked(): void {
    this.confirmationService.confirmThis(
      `${this.translationService.instant(T.settings.employees_departments_org.delete_external_user_confirmation)}`,
      () => {
        this.employeeSettingsService.deleteEmployees([this.employeeDetailsViewModel.id]).subscribe(() => {
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Delete,
            [{ id: this.employeeDetailsViewModel.id, title: this.employeeDetailsViewModel.salutation, relatedObjectId: 0 }],
            FilterTypes.External_User
          );
          this.alertService.success(this.translationService.instant(T.settings.employees_departments_org.external_user_deleted));
          this.onDelete.next({ employee: this.employeeDetailsViewModel, promoteToEmployee: false });
          this.bsModalRef.hide();
        });
      },
      () => {
        //handle cancel
      },
      this.translationService.instant(T.settings.employees_departments_org.permanent_delete_external_user),
      true
    );
  }

  public saveNewUserClicked(): void {
    this.saveButtonLoader = true;
    this.employeeDetailsViewModel.isExternal = true;
    this.employeeDetailsViewModel.accountId = this.authenticationService.getCurrentAccount().id;
    this.employeeDetailsViewModel = { ...this.employeeDetailsViewModel, ...this.form.getRawValue() } as EmployeeDetailsViewModel;

    if (this.editMode) {
      this.editExternalUser();
    } else {
      this.addExternalUser();
    }
  }

  public saveExistingUserClicked(): void {
    this.onAddExistingUser.next(this.filters);
    this.bsModalRef.hide();
  }

  public handleFilterChange(filters: FilterViewModel[]): void {
    this.filters = filters;
  }

  private addExternalUser(): void {
    this.employeeSettingsService.addEmployee(this.employeeDetailsViewModel).subscribe((data) => {
      this.saveButtonLoader = false;
      if (!data.errorList || !data.errorList.length) {
        this.allowedFiltersService.refreshFilters(
          OperationTypes.Create,
          [{ id: data.returnModel.id, title: this.employeeDetailsViewModel.firstName + ' ' + this.employeeDetailsViewModel.surname, relatedObjectId: 0 }],
          FilterTypes.External_User
        );
        this.employeeDetailsViewModel = data.returnModel;
        this.alertService.success(this.translationService.instant(T.settings.employees_departments_org.external_user_added));
        if (this.useOptions) {
          this.onCreateAndAddExternalUser.next(data.returnModel);
        } else {
          this.onAdd.next(this.employeeDetailsViewModel);
        }
        this.bsModalRef.hide();
      } else {
        void this.alertService.error(data.errorList[0]);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private setSaveButtonText(): void {
    if (this.useOptions) {
      this.saveButtonText = this.translationService.instant(T.common.assign);
    } else if (!this.useOptions && this.selectedOption === Options.newUser) {
      this.saveButtonText = this.translationService.instant(T.common.save);
    }
  }

  private editExternalUser(): void {
    this.employeeSettingsService.updateEmployeeFullDetails(this.employeeDetailsViewModel).subscribe({
      next: () => {
        this.saveButtonLoader = false;
        this.allowedFiltersService.refreshFilters(
          OperationTypes.Update,
          [{ id: this.employeeDetailsViewModel.id, title: `${this.employeeDetailsViewModel.firstName} ${this.employeeDetailsViewModel.surname}`, relatedObjectId: 0 }],
          FilterTypes.External_User,
          true
        );
        this.alertService.success(this.translationService.instant(T.settings.employees_departments_org.external_user_updated));
        this.onEdit.next(this.employeeDetailsViewModel);
        this.bsModalRef.hide();
      },
      error: (error) => {
        this.saveButtonLoader = false;
        const errorMessage = error?.error?.Message;
        if (errorMessage) {
          void this.alertService.error(errorMessage);
        }
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  public promoteToEmployee(): void {
    const bsModalRef = this.modalService.show(ManageEmployeeComponent, {
      initialState: {
        promoteExternalUser: true,
        employeeDetailsViewModel: JSON.parse(JSON.stringify(this.employeeDetailsViewModel)),
      },
      class: 'modal-full-screen',
    });
    bsModalRef.content.onSave.subscribe((res: EmployeeDetailsViewModel) => {
      this.onDelete.next({ employee: this.employeeDetailsViewModel, promoteToEmployee: true });
      this.bsModalRef.hide();
    });
  }

  public updateOption(option: Options): void {
    this.selectedOption = option;
    this.changeDetectorRef.markForCheck();
  }

  private initForm(): void {
    this.form = this.fb.group<ExternalUserFormControls>({
      firstName: this.fb.control<string>(this.employeeDetailsViewModel.firstName, {
        nonNullable: true,
        validators: [
          Validators.required,
          this.wtValidators.textInput(1, 80, WtValidators.RESTRICTED_CHARS.DEFAULT)
        ]
      }),
      surname: this.fb.control<string>(this.employeeDetailsViewModel.surname, {
        nonNullable: true,
        validators: [
          Validators.required,
          this.wtValidators.textInput(1, 80, WtValidators.RESTRICTED_CHARS.DEFAULT)
        ]
      }),
      username: this.fb.control<string>(this.employeeDetailsViewModel.username, {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.email,
          Validators.maxLength(200)
        ]
      }),
    });
  }

  private _populateForm(): void {
    this.form.patchValue({
      firstName: this.employeeDetailsViewModel.firstName,
      surname: this.employeeDetailsViewModel.surname,
      username: this.employeeDetailsViewModel.username,
    });
  }

  private initializeFilters(): void {
    if (this.currentlySelectedExternalUserId) {
      const externalUserFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.External_User);
      const currentFilter = externalUserFilters.find(f => f.filterValue?.toString() === this.currentlySelectedExternalUserId?.toString());
      if (currentFilter) {
        this.filters = [currentFilter];
        this.changeDetectorRef.markForCheck();
      }
    }
  }
}
